import { useState } from 'react';
import { ActivityIndicator, StyleSheet } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { FormInput } from '../../../components/primitives/FormInput';
import { Sheet } from '../../../components/primitives/Sheet';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { spacing } from '../../../theme/spacing';
import { tokens } from '../../../theme/tokens';
import { fieldPlaceholderColor } from '../../../theme/typography';
import { COMPANY_SIZE_OPTIONS } from '../constants';
import type { SegmentCriteria } from '../types';

interface SegmentCriteriaFormProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (name: string, criteria: SegmentCriteria) => void;
  isLoading?: boolean;
  initialName?: string;
  initialCriteria?: SegmentCriteria;
}

export function SegmentCriteriaForm({
  visible,
  onClose,
  onSubmit,
  isLoading,
  initialName = '',
  initialCriteria = {},
}: Readonly<SegmentCriteriaFormProps>) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [keywords, setKeywords] = useState(initialCriteria.keywords || '');
  const [location, setLocation] = useState(initialCriteria.location || '');
  const [industry, setIndustry] = useState(initialCriteria.industry || '');
  const [companySize, setCompanySize] = useState(initialCriteria.companySize || '');
  const [roleFilter, setRoleFilter] = useState(initialCriteria.roleFilter || '');
  const [maxResults, setMaxResults] = useState(String(initialCriteria.maxResults || 50));

  const isValid = name.trim().length >= 3 && (keywords.trim() || roleFilter.trim());

  const handleSubmit = () => {
    if (!isValid || isLoading) return;

    const criteria: SegmentCriteria = {
      ...(keywords.trim() && { keywords: keywords.trim() }),
      ...(location.trim() && { location: location.trim() }),
      ...(industry.trim() && { industry: industry.trim() }),
      ...(companySize && { companySize }),
      ...(roleFilter.trim() && { roleFilter: roleFilter.trim() }),
      maxResults: Math.min(Math.max(Number.parseInt(maxResults, 10) || 50, 1), 200),
    };

    onSubmit(name.trim(), criteria);
  };

  return (
    <Sheet visible={visible} onClose={onClose} title={t('linkedinSearch.form.title')} size="lg">
      <VStack spacing="lg" style={styles.content}>
        <VStack spacing="xs">
          <Text variant="caption" color={colors.text.secondary} style={styles.label}>
            {t('linkedinSearch.form.segmentName')}
          </Text>
          <FormInput
            value={name}
            onChangeText={setName}
            placeholder={t('linkedinSearch.form.segmentNamePlaceholder')}
            placeholderTextColor={fieldPlaceholderColor}
            style={styles.input}
            maxLength={200}
            accessibilityLabel={t('linkedinSearch.form.segmentName')}
          />
        </VStack>

        <VStack spacing="xs">
          <Text variant="caption" color={colors.text.secondary} style={styles.label}>
            {t('linkedinSearch.form.keywords')}
          </Text>
          <FormInput
            value={keywords}
            onChangeText={setKeywords}
            placeholder={t('linkedinSearch.form.keywordsPlaceholder')}
            placeholderTextColor={fieldPlaceholderColor}
            style={styles.input}
            accessibilityLabel={t('linkedinSearch.form.keywords')}
          />
        </VStack>

        <VStack spacing="xs">
          <Text variant="caption" color={colors.text.secondary} style={styles.label}>
            {t('linkedinSearch.form.roleFilter')}
          </Text>
          <FormInput
            value={roleFilter}
            onChangeText={setRoleFilter}
            placeholder={t('linkedinSearch.form.roleFilterPlaceholder')}
            placeholderTextColor={fieldPlaceholderColor}
            style={styles.input}
            accessibilityLabel={t('linkedinSearch.form.roleFilter')}
          />
        </VStack>

        <VStack spacing="xs">
          <Text variant="caption" color={colors.text.secondary} style={styles.label}>
            {t('linkedinSearch.form.location')}
          </Text>
          <FormInput
            value={location}
            onChangeText={setLocation}
            placeholder={t('linkedinSearch.form.locationPlaceholder')}
            placeholderTextColor={fieldPlaceholderColor}
            style={styles.input}
            accessibilityLabel={t('linkedinSearch.form.location')}
          />
        </VStack>

        <VStack spacing="xs">
          <Text variant="caption" color={colors.text.secondary} style={styles.label}>
            {t('linkedinSearch.form.industry')}
          </Text>
          <FormInput
            value={industry}
            onChangeText={setIndustry}
            placeholder={t('linkedinSearch.form.industryPlaceholder')}
            placeholderTextColor={fieldPlaceholderColor}
            style={styles.input}
            accessibilityLabel={t('linkedinSearch.form.industry')}
          />
        </VStack>

        <VStack spacing="xs">
          <Text variant="caption" color={colors.text.secondary} style={styles.label}>
            {t('linkedinSearch.form.companySize')}
          </Text>
          <HStack spacing="xs" style={styles.chipRow}>
            {COMPANY_SIZE_OPTIONS.map((size) => (
              <Box
                key={size}
                pressable
                onPress={() => setCompanySize(companySize === size ? '' : size)}
                borderRadius="md"
                padding="xs"
                backgroundColor={companySize === size ? colors.primary.muted : 'transparent'}
                style={[styles.chip, companySize === size && styles.chipSelected]}
                accessibilityRole="button"
                accessibilityLabel={`Company size: ${size}`}
                accessibilityState={{ selected: companySize === size }}
              >
                <Text
                  variant="caption"
                  color={companySize === size ? colors.primary[400] : colors.text.tertiary}
                >
                  {size}
                </Text>
              </Box>
            ))}
          </HStack>
        </VStack>

        <VStack spacing="xs">
          <Text variant="caption" color={colors.text.secondary} style={styles.label}>
            {t('linkedinSearch.form.maxResults')}
          </Text>
          <FormInput
            value={maxResults}
            onChangeText={setMaxResults}
            placeholder="50"
            placeholderTextColor={fieldPlaceholderColor}
            style={styles.input}
            keyboardType="numeric"
            maxLength={3}
            accessibilityLabel={t('linkedinSearch.form.maxResults')}
          />
        </VStack>

        <Button onPress={handleSubmit} disabled={!isValid || isLoading} variant="primary" fullWidth>
          {isLoading ? (
            <ActivityIndicator size="small" color={colors.background.primary} />
          ) : (
            t('linkedinSearch.form.createSegment')
          )}
        </Button>
      </VStack>
    </Sheet>
  );
}

const styles = StyleSheet.create({
  content: {
    paddingTop: spacing.sm,
  },
  label: {
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    backgroundColor: colors.background.tertiary,
    borderRadius: tokens.radius.smPlus,
    borderWidth: 1,
    borderColor: colors.border.default,
    paddingHorizontal: spacing.md,
    paddingVertical: tokens.spacing.smPlus,
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
  },
  chipRow: {
    flexWrap: 'wrap',
  },
  chip: {
    borderWidth: 1,
    borderColor: colors.border.default,
    marginBottom: spacing.xs,
  },
  chipSelected: {
    borderColor: colors.primary.subtle,
  },
});
